/**
 * CobroMuelleCxCDialog — repartir el saldo pendiente de una reserva.
 *
 * Acá NO se cobra. Lo que se corrige es la previsión: cuánto va a pagar de
 * verdad el pasajero en el muelle, y qué pasa con la diferencia. Esa es toda
 * la operación.
 *
 * El saldo se reparte en tres destinos que suman el total:
 *
 *     Se cobra en el muelle   →  sigue pendiente ahí, lo cobra recepción
 *     Cruce con comisión      →  se compensa contra comisión que Entretur le
 *                                debe a la agencia (reserva y valor concretos)
 *     Cuenta por cobrar       →  la agencia responde, contra su cupo
 *
 * El cobro real —con su soporte, su método, su cuenta y su cierre de caja—
 * vive en Muelle → Recepción, que acepta cualquier fecha. Pedir el soporte acá
 * daría a entender que el pago se está haciendo en esta pantalla, y no es así:
 * esto es una corrección de cómo queda repartido el saldo.
 *
 * Todo pasa por un endpoint que valida antes de escribir y deja auditoría y
 * script de reversa:
 *   POST /turismo/correcciones/reservas/{id}/cobro-muelle-y-cxc
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, TextField, Alert, CircularProgress, Stack, Chip,
  Checkbox, FormControlLabel,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

export default function CobroMuelleCxCDialog({
  open, onClose, reserva, onHecho, onToast,
}) {
  const saldo = Number(reserva?.saldo_pendiente || 0);
  const agencia = reserva?.agencia_nombre || reserva?.agencia?.nombre || 'la agencia';
  const diaSalida = String(reserva?.salida?.fecha || reserva?.salida_fecha || '').slice(0, 10);

  // Cuánto se va a cobrar en el muelle. Arranca en el saldo completo: el caso
  // por defecto es que no se reparte nada y lo paga todo el pasajero.
  const [muelle, setMuelle] = useState('0');
  const [cruce, setCruce] = useState('0');
  const [comisiones, setComisiones] = useState([]);
  const [comisionesSel, setComisionesSel] = useState([]);
  const [cargandoComis, setCargandoComis] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMuelle(String(Number(reserva?.saldo_pendiente || 0)));
    setCruce('0'); setComisionesSel([]); setError(null);
  }, [open, reserva?.saldo_pendiente]);

  // Las comisiones que Entretur todavía le debe a esta agencia. Es la misma
  // lista que usa la pantalla de cruce en Finanzas, así que las cifras
  // coinciden y no hay dos verdades.
  useEffect(() => {
    if (!open || !reserva?.agencia_id) { setComisiones([]); return; }
    setCargandoComis(true);
    fetch(`${API_BASE}/turismo/cruce/agencia/${reserva.agencia_id}`
          + `?empresa_id=${reserva.empresa_id}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setComisiones(d.comisiones_pendientes || []))
      .catch(() => setComisiones([]))
      .finally(() => setCargandoComis(false));
  }, [open, reserva?.agencia_id, reserva?.empresa_id]);

  // Pesos enteros: se bota todo lo que no sea dígito. Antes se dejaba pasar el
  // punto, así que escribir «120.000» —como se escribe acá— se leía como $120
  // y el reparto salía mal sin avisar.
  const aNumero = (x) => Number(String(x).replace(/\D/g, '')) || 0;

  // Lo que queda escrito en el campo. Se filtra al teclear, no al calcular:
  // antes las letras se quedaban a la vista aunque la cuenta las ignorara, y
  // uno veía «abc» en el campo con $0 en el reparto sin entender por qué.
  // El cero de arranque se absorbe, para que teclear sobre él dé 640000 y no
  // 0640000. Vacío se permite —se lee como 0— para poder reescribir el monto.
  const soloDigitos = (x) => String(x).replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  const muelleNum = aNumero(muelle);
  const cruceNum = aNumero(cruce);

  // Lo que se cruza sale del muelle, que es el residual natural: si el pasajero
  // iba a pagar los $680.000 y se cruzan $640.000, en el muelle quedan $40.000.
  // Tener que bajarlo a mano no sólo era incómodo —dejaba el diálogo en error
  // mientras no cuadraran las cifras.
  //
  // Lo que NO se toca es la CxC que el usuario ya hubiera declarado bajando el
  // muelle: si dejó una parte a cargo de la agencia, mover el cruce no se la
  // come. Se recalcula desde el saldo en vez de restar sobre lo que haya, así
  // que corregir el monto del cruce varias veces no acumula.
  const cambiarCruce = (valor) => {
    const nuevo = aNumero(valor);
    const cxcDeclarada = Math.max(saldo - muelleNum - cruceNum, 0);
    setCruce(valor);
    setMuelle(String(Math.max(saldo - nuevo - cxcDeclarada, 0)));
  };
  // La CxC es lo que nadie más se llevó: si el pasajero paga menos y no se
  // cruza, la diferencia la responde la agencia.
  const cxcNum = Math.max(saldo - muelleNum - cruceNum, 0);

  const comisionMarcada = comisiones
    .filter(c => comisionesSel.includes(c.id))
    .reduce((acc, c) => acc + Number(c.comision || 0), 0);

  // Cómo se va a repartir el monto del cruce entre las comisiones marcadas.
  //
  // Se recorre `comisiones` en el orden en que llegó —FIFO, la más vieja
  // primero—, que es el mismo orden en que el backend las consume. Importa
  // cuando el monto no alcanza para todas: ahí hay que poder decir en qué
  // reserva concreta queda el remanente, y no sólo cuánto sobra.
  const repartoCruce = React.useMemo(() => {
    let restante = cruceNum;
    const out = {};
    for (const c of comisiones) {
      if (!comisionesSel.includes(c.id)) continue;
      const pendiente = Number(c.comision || 0);
      const usa = Math.min(pendiente, Math.max(restante, 0));
      out[c.id] = { usa, queda: pendiente - usa };
      restante -= usa;
    }
    return out;
  }, [comisiones, comisionesSel, cruceNum]);

  // Por cómo reparte el FIFO hay a lo sumo UNA comisión mordida a medias: las
  // anteriores se consumen enteras y las siguientes no se tocan. Esa es la que
  // hay que nombrar —es donde queda el remanente—. Las que no se alcanzan a
  // tocar ya se marcan en su propia fila; repetirlas acá sólo haría ruido.
  const comisionParcial = comisiones.find(
    c => (repartoCruce[c.id]?.usa || 0) > 0 && (repartoCruce[c.id]?.queda || 0) > 0);
  const sinTocar = comisiones.filter(
    c => comisionesSel.includes(c.id) && (repartoCruce[c.id]?.usa || 0) === 0);

  // Al asiento sólo van las comisiones que el cruce realmente consume. Mandar
  // también las marcadas que no se tocan infla el corte —cuenta reservas que
  // no se saldaron— sin cambiar nada de la plata.
  const comisionesUsadas = comisiones
    .filter(c => (repartoCruce[c.id]?.usa || 0) > 0).map(c => c.id);

  const excede = muelleNum + cruceNum > saldo + 0.01;
  const cruceSinComision = cruceNum > 0 && comisionesSel.length === 0;
  const cruceExcedeComision = cruceNum > comisionMarcada + 0.01;
  const sinCambio = muelleNum === saldo && cruceNum === 0;
  const puedeGuardar = !excede && !cruceSinComision && !cruceExcedeComision
    && !sinCambio && !guardando;

  const toggleComision = (id) => setComisionesSel(
    sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/correcciones/reservas/${reserva.id}/cobro-muelle-y-cxc`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({
            // Cero siempre: acá no entra plata. Lo que va al muelle queda
            // PENDIENTE de cobro, y eso el servicio lo deduce del resto.
            monto_muelle: 0,
            metodo_pago: 'efectivo',
            monto_cruce: cruceNum,
            comisiones_reservacion_ids: cruceNum > 0 ? comisionesUsadas : null,
            monto_cxc: cxcNum,
          }),
        });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.message || d.detail || 'No se pudo repartir el saldo');

      const partes = [];
      if (muelleNum > 0) partes.push(`${formatPrecio(muelleNum, reserva.moneda)} se cobran en el muelle`);
      if (cruceNum > 0) partes.push(`${formatPrecio(cruceNum, reserva.moneda)} cruzados con comisión`);
      if (cxcNum > 0) partes.push(`${formatPrecio(cxcNum, reserva.moneda)} a cargo de ${agencia}`);
      onToast?.success?.(partes.join(' · ') || 'Saldo repartido.');
      onHecho?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'No se pudo repartir el saldo');
    }
    setGuardando(false);
  };

  return (
    <Dialog open={!!open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Repartir el saldo pendiente
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {reserva?.codigo_reserva} · por cobrar: {formatPrecio(saldo, reserva?.moneda)}
          {diaSalida ? ` · pasadía ${diaSalida}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#ecfeff', border: '1px solid #a5f3fc' }}>
            <Typography sx={{ fontSize: 12, color: '#155e75' }}>
              Acá no se registra ningún pago. Se declara <b>cuánto va a pagar el pasajero en el
              muelle</b> y qué pasa con la diferencia. El cobro, con su soporte y su cuenta, se
              registra en <b>Muelle → Recepción</b> el día del pasadía.
            </Typography>
          </Box>

          <TextField label="Se va a cobrar en el muelle" size="small"
                     value={muelle}
                     onChange={(e) => setMuelle(soloDigitos(e.target.value))}
                     inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                     error={excede}
                     helperText={excede
                       ? `No puede pasar del saldo de ${formatPrecio(saldo, reserva?.moneda)}`
                       : `De los ${formatPrecio(saldo, reserva?.moneda)} pendientes`} />

          {/* ── Cruce con comisión ──────────────────────────────────────────
              Entretur le debe comisiones a la agencia y la agencia responde por
              este saldo: en vez de que cada uno pague lo suyo, se compensan.
              Solo aparece si hay comisión pendiente contra la que cruzar. ── */}
          {reserva?.agencia_id && (cargandoComis || comisiones.length > 0) && (
            <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#fffbeb',
                       border: '1px solid #fde68a' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <SwapHorizIcon sx={{ fontSize: 17, color: '#b45309' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                  Cruzar con comisión que se le debe a {agencia}
                </Typography>
                {cargandoComis && <CircularProgress size={13} />}
              </Box>

              {comisiones.length > 0 && (
                <>
                  <TextField label="Cuánto se cruza" size="small" fullWidth
                             value={cruce}
                             onChange={(e) => cambiarCruce(soloDigitos(e.target.value))}
                             inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                             error={cruceExcedeComision}
                             helperText={cruceExcedeComision
                               ? `Las comisiones marcadas suman ${formatPrecio(comisionMarcada, reserva?.moneda)}`
                               : 'Se descuenta de lo que se cobra en el muelle. Déjalo en 0 si no se cruza nada'}
                             sx={{ mb: 1 }} />

                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#92400e',
                                    textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Contra cuál comisión
                  </Typography>
                  {cruceSinComision && (
                    <Typography sx={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>
                      Marca al menos una.
                    </Typography>
                  )}
                  <Box sx={{ maxHeight: 148, overflowY: 'auto', mt: 0.25 }}>
                    {comisiones.map(c => (
                      <FormControlLabel key={c.id}
                        control={<Checkbox size="small" checked={comisionesSel.includes(c.id)}
                                           onChange={() => toggleComision(c.id)} />}
                        label={
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                              {formatPrecio(c.comision, reserva?.moneda)}
                              <Box component="span" sx={{ fontWeight: 500, color: '#64748b' }}>
                                {' · '}{c.codigo}
                              </Box>
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
                              {c.cliente}{c.paquete ? ` · ${c.paquete}` : ''}
                            </Typography>
                            {/* Qué le toca a esta comisión del monto a cruzar.
                                Sin esto, "quedan $40.000" no decía de cuál. */}
                            {comisionesSel.includes(c.id) && cruceNum > 0 && (
                              <Typography sx={{ fontSize: 10.5, fontWeight: 700,
                                color: (repartoCruce[c.id]?.queda || 0) > 0 ? '#b45309' : '#15803d' }}>
                                {(repartoCruce[c.id]?.usa || 0) === 0
                                  ? 'No alcanza: queda completa'
                                  : (repartoCruce[c.id]?.queda || 0) > 0
                                    ? `Se cruzan ${formatPrecio(repartoCruce[c.id].usa, reserva?.moneda)}`
                                      + ` · quedan ${formatPrecio(repartoCruce[c.id].queda, reserva?.moneda)}`
                                    : `Se cruza completa`}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ display: 'flex', alignItems: 'flex-start', mr: 0, py: 0.15 }} />
                    ))}
                  </Box>
                  {comisionesSel.length > 0 && (
                    <Typography sx={{ fontSize: 10.5, color: '#92400e', mt: 0.5 }}>
                      Marcadas: {formatPrecio(comisionMarcada, reserva?.moneda)} de comisión.
                      {cruceNum > 0 && comisionParcial && (
                        <> Quedan {formatPrecio(repartoCruce[comisionParcial.id].queda, reserva?.moneda)}
                          {' '}en <b>{comisionParcial.codigo}</b> para otro cruce.
                        </>
                      )}
                      {cruceNum > 0 && sinTocar.length > 0 && (
                        <> {sinTocar.length === 1
                            ? `${sinTocar[0].codigo} no se alcanza a tocar.`
                            : `${sinTocar.length} marcadas no se alcanzan a tocar.`}
                        </>
                      )}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          {/* El reparto, a la vista, para que nadie confirme a ciegas */}
          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#f8fafc',
                     border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#475569',
                              textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.75 }}>
              Cómo queda el saldo de {formatPrecio(saldo, reserva?.moneda)}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`Se cobra en muelle: ${formatPrecio(muelleNum, reserva?.moneda)}`}
                    sx={{ bgcolor: muelleNum > 0 ? '#f0fdf4' : '#f1f5f9',
                          color: muelleNum > 0 ? '#166534' : '#64748b', fontWeight: 700 }} />
              {cruceNum > 0 && (
                <Chip size="small" label={`Cruzado con comisión: ${formatPrecio(cruceNum, reserva?.moneda)}`}
                      sx={{ bgcolor: '#fffbeb', color: '#92400e', fontWeight: 700 }} />
              )}
              <Chip size="small"
                    label={`Cuenta por cobrar ${agencia}: ${formatPrecio(cxcNum, reserva?.moneda)}`}
                    sx={{ bgcolor: cxcNum > 0 ? '#ecfeff' : '#f1f5f9',
                          color: cxcNum > 0 ? '#0e7490' : '#64748b', fontWeight: 700 }} />
            </Stack>
            {excede && (
              <Typography sx={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700, mt: 0.75 }}>
                El muelle más el cruce se pasan del saldo.
              </Typography>
            )}
            {sinCambio && !excede && (
              <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.75 }}>
                Así está hoy: todo el saldo se cobra en el muelle. Baja ese monto para
                repartir la diferencia.
              </Typography>
            )}
            {cxcNum > 0 && (
              <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.75 }}>
                Los {formatPrecio(cxcNum, reserva?.moneda)} se cargan contra el cupo de {agencia}.
                Si no le alcanza, el sistema lo frena y te dice cuánto tiene disponible.
              </Typography>
            )}
            {cruceNum > 0 && !cruceSinComision && !cruceExcedeComision && (
              <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.75 }}>
                El cruce deja el asiento completo: corte de liquidación, registro del cruce
                y un pago con origen «cruce» en esta reserva. Se puede revertir.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" startIcon={<PaymentsIcon />}
                disabled={!puedeGuardar} onClick={guardar}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
          {guardando ? 'Guardando…' : 'Repartir el saldo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
